import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { useSpace } from '../../../hooks/useSpace';
import { keys, type AllDocumentsParams } from './keys';

async function fetchAllDocuments(spaceId: string, params: AllDocumentsParams) {
  const from = params.page * params.pageSize;
  const to = from + params.pageSize - 1;

  // Documents are self-scoped by space_id, so a single filter returns every
  // document type (subject, provider contract, meeting report) in the space.
  const sortColumn = params.sortField === 'type' ? 'document_type_id' : params.sortField;

  let query = apiClient
    .from('documents')
    .select('*, providers:provider_id(name), sub_topics:sub_topic_id(id, name), document_types:document_type_id(id, name)', {
      count: 'exact',
    })
    .eq('space_id', spaceId)
    .order(sortColumn, { ascending: params.sortDirection === 'asc' })
    .range(from, to);

  if (params.search) {
    query = query.ilike('name', `%${params.search}%`);
  }

  if (params.type) {
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(params.type);
    if (isUuid) {
      query = query.eq('document_type_id', params.type);
    } else {
      const { data: dt } = await apiClient
        .from('document_types')
        .select('id')
        .eq('space_id', spaceId)
        .eq('name', params.type)
        .maybeSingle();
      if (dt) {
        query = query.eq('document_type_id', dt.id);
      }
    }
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  return { data: data ?? [], count: count ?? 0 };
}

export function useGetAllDocuments(params: AllDocumentsParams) {
  const { currentSpace } = useSpace();

  return useQuery({
    queryKey: keys.getAllDocuments(currentSpace?.id ?? '', params),
    queryFn: () => fetchAllDocuments(currentSpace!.id, params),
    placeholderData: keepPreviousData,
    enabled: !!currentSpace?.id,
  });
}
